import { Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { memo } from 'react';
import { useSelector } from 'react-redux';
import CartItemDetail from '../../components/ItemBuilder/menu/CartItemDetail';
import { selectCartItems } from '../../selectors/cart';
import { selectSelectedItemCartId } from '../../selectors/dialog';
import { SetCartItemRef } from '../../types';
import { useAppSelector } from '../../app/hooks';
import { selectAutoComboCartItems } from '../../redux/features/autoCombo/autoCombo.selector';
import { DRAFT_CART_SCOPE } from '../../redux/features/draftCart/cartScope';
import { selectWriteCartScope } from '../../redux/features/draftCart/cartScope.routing';
import { selectDraftCartItems } from '../../redux/features/draftCart/draftCart.selector';
import { DraftCartScope } from '../../components/OrderReview/CartScopeContext';
import { isValidCartSelector } from '../../utils/cart';
import useAutoCombo from '../../hooks/autoCombo/useAutoCombo.hooks';

const useStyles = makeStyles(({ spacing }: Theme) => ({
  conatiner: {
    padding: `${spacing(7)} ${spacing(1)} 0 ${spacing(1)}`,
  },
  title: {
    fontStyle: 'italic',
  },
}));

const withScope = (isDraft: boolean, children: JSX.Element) =>
  isDraft ? <DraftCartScope>{children}</DraftCartScope> : children;

interface OwnProps {
  setCartItemRef: SetCartItemRef;
}

const ItemBuilder = memo(function (props: OwnProps) {
  const { setCartItemRef } = props;
  const classes = useStyles(props);

  const cartItems = useSelector(selectCartItems);
  const draftCartItems = useAppSelector(selectDraftCartItems);
  const selectedItemCartId = useSelector(selectSelectedItemCartId);
  const validCart = useSelector(isValidCartSelector);
  const autoCombinedCartItems = useAppSelector(selectAutoComboCartItems);
  // HITL-1617: `selectedItemCartId` is one number over TWO id spaces now, and
  // both carts number from 1. Resolving it against `state.cart` alone would open
  // the AI's item 2 when the agent clicked My Cart's item 2.
  const writeScope = useAppSelector(selectWriteCartScope);
  const isDraft = writeScope === DRAFT_CART_SCOPE;

  const { isAutoComboEnabled } = useAutoCombo();

  // Auto-combo is a property of the ORDER — `autoCombo` reads `state.cart` and
  // its combined lines are what gets sent to the POS. A draft has none, so the
  // branch is main-cart only rather than merely unlikely to match.
  const cartItem = isDraft
    ? draftCartItems?.[selectedItemCartId]
    : validCart &&
      isAutoComboEnabled &&
      Object.values(autoCombinedCartItems).length
    ? autoCombinedCartItems[selectedItemCartId]
    : cartItems?.[selectedItemCartId];

  return (
    // HITL-1530: `data-alt-readout-scope` marks this subtree as the only place
    // Alt+click reads a label out. The center pane is where the menu lives —
    // sizes, options, modifier choices — so it is the only pane whose labels
    // answer a customer's question. The cart, the action bar and the header
    // carry controls that act rather than inform, and stay untouched.
    <div id="itemBuilder" className={classes.conatiner} data-alt-readout-scope>
      {selectedItemCartId && cartItem && !cartItem.autoCombinedCartItemIds
        ? // HITL-1617: the pane declares whose row it is showing, the same way the
          // My Cart column does. `CartItemModGroups` and every other shared piece
          // below then compares its own scope rather than a bare id, so a draft
          // item's mod groups cannot light up against a main-cart selection.
          withScope(
            isDraft,
            <CartItemDetail
              cartItem={cartItem}
              setCartItemRef={setCartItemRef}
            />
          )
        : undefined}
    </div>
  );
});

export default ItemBuilder;
